<script setup lang="ts">
import { Color } from '@previo/codebooks'
import { QItem, QItemSection } from 'quasar'
import { computed, ref, watch } from 'vue'
import { $t } from '../../../../composables/i18n'
import {
  PDateRangePickerFastChoice,
  type PDateRangePickerFastChoiceItem,
} from '../../../date-range-picker/types'
import PList from '../../../list/PList.vue'
import PTextFormBody from '../../../typography/form/PTextFormBody.vue'
import { useDateTimeRangePicker } from '../../composables/DateTimeRangePickerState'
import { termChoicesList } from '../../../date-range-picker/constants'
import { DateTimeRange } from '../../model/DateTimeRange'
import { applyTimeToDate } from '../../utils'
import { resolveDateRangeFastChoice } from './resolveDateRangeFastChoice'

type Props = {
  fastChoices?: PDateRangePickerFastChoice[]
  defaultTimeFrom?: string
  defaultTimeTo?: string
}

const props = defineProps<Props>()
const model = defineModel<DateTimeRange>({ default: new DateTimeRange() })

const navigation = useDateTimeRangePicker()

const selectedFastChoice = ref<PDateRangePickerFastChoice>()
const visibleFastChoices = computed<PDateRangePickerFastChoiceItem[]>(() => {
  const visible: PDateRangePickerFastChoiceItem[] = []

  props.fastChoices?.map((choiceType) => {
    const choice = termChoicesList.find((choice) => choice.type === choiceType)

    if (choice) {
      visible.push(choice)
    }
  })

  return visible
})

const hasFastChoice = computed<boolean>(() => {
  return visibleFastChoices.value.length > 0
})

watch(selectedFastChoice, (value) => {
  if (!value) {
    return
  }

  const { from, to } = resolveDateRangeFastChoice(value)

  model.value.from = applyTimeToDate(from, props.defaultTimeFrom)
  model.value.to = applyTimeToDate(to, props.defaultTimeTo)

  navigation.setRange(
    new DateTimeRange(
      applyTimeToDate(from, props.defaultTimeFrom),
      applyTimeToDate(to, props.defaultTimeTo),
    ),
  )
})
</script>

<template>
  <div v-if="hasFastChoice" class="p-scroll-y">
    <p-list dense>
      <template v-for="choice in visibleFastChoices" :key="choice.type">
        <q-item
          active-class="text-primary"
          :active="selectedFastChoice === choice.type"
          clickable
          @click="selectedFastChoice = choice.type"
        >
          <q-item-section class="p-cursor-pointer">
            <div class="p-flex">
              <p-text-form-body
                :text="$t(choice.name)"
                :color="selectedFastChoice === choice.type ? Color.PRIMARY : undefined"
              />
            </div>
          </q-item-section>
        </q-item>
      </template>
    </p-list>
  </div>
</template>
